import {
  type AiLog,
  type AiLogEventEnum,
  type AiStatusLog,
  AiStatusLogSchema,
  aiLogInferenceEventEnums,
  aiLogModelHealthEventEnums,
  aiLogModelResetEventEnums,
  aiLogTrainingEventEnums,
} from '@timelyapp/integration-cloud-api-spec';
import { DateTime } from 'luxon';

/**
 * Accepts logs from `queryGetAiPredictionStatusByRange` and returns status
 * of various AI model/predictions/etc.
 */
export function transformLogsToStatuses(logs: AiLog[]) {
  const { dates_updated_at, event_logs } = logs.reduce(
    (acc, log) => {
      if (log.day) acc.dates_updated_at[log.day] = log.updated_at;
      if (!log.day) acc.event_logs.push(log);
      return acc;
    },
    { dates_updated_at: {} as Record<string, string>, event_logs: [] as AiLog[] }
  );

  const status = transformRecords(event_logs);

  return { dates_updated_at, status };
}

/**
 * Returns the most recently `updated_at` record matching a given set of event types.
 *
 * Ignores records that are older than 30 minutes for `training_requested` and `inference_requested`;
 * and presumes we never heard back from AI on a request (likely deduped).
 * Finding the bug would be better than relying on this.
 */
function determineLatestState(
  records: AiLog[],
  eventTypes: AiLogEventEnum[] | ReadonlyArray<AiLogEventEnum>
): AiLog | null {
  const now = DateTime.now();
  return records
    .filter(record => eventTypes.includes(record.event))
    .reduce((latest: AiLog | null, record: AiLog) => {
      // We have the first, OR we found a more recent record
      if (!latest || new Date(record.updated_at) > new Date(latest.updated_at)) {
        const isRequestOutdated =
          ['training_requested', 'inference_requested'].includes(record.event) &&
          now.diff(DateTime.fromISO(record.updated_at)).as('minutes') >= 30;

        // If this event is considered outdated, we'll ignore it
        if (isRequestOutdated) return latest;

        return record;
      }
      return latest;
    }, null);
}

/**
 * Returns _only_ the relevant logs for the current state of the AI model.
 */
function transformRecords(records: AiLog[]): AiStatusLog[] {
  const isoDateNow = new Date().toISOString();

  // We're handling ^^ events separately, so omit from "rest of.." log
  const omitFromFinalLogs: AiLogEventEnum[] = [
    ...aiLogTrainingEventEnums,
    ...aiLogInferenceEventEnums,
    ...aiLogModelResetEventEnums,
    ...aiLogModelHealthEventEnums,
  ];

  const trainingLatest = determineLatestState(records, aiLogTrainingEventEnums);
  const inferenceLatest = determineLatestState(records, aiLogInferenceEventEnums);
  const modelResetLatest = determineLatestState(records, aiLogModelResetEventEnums);
  const modelHealthLatest = determineLatestState(records, aiLogModelHealthEventEnums);

  const remainingRecords = records.filter(
    record => !omitFromFinalLogs.includes(record.event) && !!record.updated_at
  );

  if (modelHealthLatest) {
    remainingRecords.push(modelHealthLatest);
  }

  if (modelResetLatest) {
    remainingRecords.push(modelResetLatest);
  }

  if (trainingLatest) {
    remainingRecords.push(trainingLatest);
  } else {
    remainingRecords.push({
      day: null,
      event: 'training_uninitialized',
      message: 'Not yet trained',
      updated_at: isoDateNow,
    });
  }
  if (inferenceLatest) {
    remainingRecords.push(inferenceLatest);
  } else {
    remainingRecords.push({
      day: null,
      event: 'inference_uninitialized',
      message: 'Not yet inferred',
      updated_at: isoDateNow,
    });
  }

  return remainingRecords.map(record => {
    return AiStatusLogSchema.parse({
      ...record,
      at: record.updated_at ? new Date(record.updated_at).toISOString() : null,
    });
  });
}
